import type { Underlying } from 'apiConnector/secondary-market/undelyingsApiConnector';
import type { EditableFieldInfo, IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { emptyBlotterPreferences } from 'components/shared/Blotter/Grid/blotter.model';
import type { Custodian } from 'models/custodian';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import type { MakePartial } from 'types/utils';
import type { LoadingStatus } from 'utils/status';

export const SECONDARY_MARKET_PRODUCTS_BLOTTER_MAX_ITEMS = 1_000;

export type ProductCodeType = 'ISIN' | 'File Code' | 'ELIOT' | 'ELIOT OTC' | 'WKN' | 'VALOREN' | 'CUSIP' | 'XONE';
export type ApiCodeType = 'ISIN' | 'File Code' | 'ELIOT' | 'ELIOT OTC' | 'WKN' | 'VALOREN' | 'CUSIP';
export type SecondaryQuotationType = 'Clean' | 'Dirty';
export type SecondaryPriceType = 'Amount' | 'Percent';

export type SecondaryMarketProductGreeks = {
  underlyingBcsKey?: string;
  spot?: number;
  spotDate?: string;
  performance?: number;
  underlyingLevel?: number;
  label?: string;
  bloombergTicker?: string;
  currency?: string;
  underlyingInitialPrice?: number;
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  vol?: number;
  isWorst?: boolean;
};
export type SecondaryMarketProductInternalUser = SecondaryMarketProductExternalUser &
  Readonly<
    Partial<{
      launchDate: string;
      issuedQty: number;
      outstandingQty: number;
      quotationType: SecondaryQuotationType;
      accruedInterest: number;
      name: string;
      bidCp: number;
      askCp: number;
      cobDate: string;
      validationDate: string;
      salesMarginCp: number;
      upfrontCp: number;
      bidLastRt: number;
      askLastRt: number;
      updateTimeLastRt: string;
      salesMarginLastRt: number;
      upfrontLastRt: number;
      cpTradeAuthorisation: string;
      rtTradeAuthorisation: string;
      closePriceGreeks: SecondaryMarketProductGreeks[];
      liveGreeks: SecondaryMarketProductGreeks[];
    }>
  >;

export type SecondaryMarketProductExternalUser = Readonly<
  {
    rowId: string;
    searchKey: string;
  } & Partial<{
    bcsKeySource: string;
    bcsKeyPrefixedCode: string;
    isin: string;
    ric: string;
    valoren: string;
    wkn: string;
    cusip: string;
    fileCode: string;
    eliot: string;
    eliotOtc: string;
    maturityDate: string;
    denomination: string;
    currency: string;
    salesMessage: string;
    tradeMessage: string;
    quotationType: SecondaryQuotationType;
    priceType: SecondaryPriceType;
    limitOrderStatus: boolean;
    client: ISalesCounterpart;
    custodian: Custodian;
    hasDlpSecondaryMarket: boolean;
  }>
>;

export type SecondaryMarketProduct = SecondaryMarketProductInternalUser | SecondaryMarketProductExternalUser;

export type SecondaryMarketProductCode = Readonly<{
  type: ProductCodeType;
  code: string;
}>;

export type SecondaryMarketProductEditFields = Partial<Record<keyof SecondaryMarketProduct, EditableFieldInfo>>;
export type ProductSearchEditState = {
  items: Record<string, SecondaryMarketProductEditFields>;
};

export type LoadableUnderlying = MakePartial<Underlying, 'currency' | 'label' | 'isin'> & {
  loadingStatus: LoadingStatus;
};
export type ProductSearchState = Readonly<{
  codeType?: ProductCodeType;
  items: readonly SecondaryMarketProduct[];
  notFoundKeys: readonly string[];
  searchingKeys: readonly string[];
  refreshingProductRowIds: readonly string[];
  refreshedProductRowIds: readonly string[];
  hasTechnicalError: boolean;
  errorMessage?: string;
  timeout?: number;
  columns: readonly IBlotterColumnDefinition<SecondaryMarketProduct>[];
  canSaveBlotterPreferences: boolean;
  areBlotterPreferencesInError: boolean;
  isPreferenceStatePersistenceFailed: boolean;
  blotterPreferenceState: IBlotterPreferenceState;
  editState: ProductSearchEditState;
  underlyingsDetails: Record<string, LoadableUnderlying>;
  focusedProductId?: string;
}>;

export const initialProductSearchState: ProductSearchState = {
  blotterPreferenceState: emptyBlotterPreferences,
  areBlotterPreferencesInError: false,
  canSaveBlotterPreferences: false,
  isPreferenceStatePersistenceFailed: false,
  items: [],
  searchingKeys: [],
  notFoundKeys: [],
  refreshedProductRowIds: [],
  refreshingProductRowIds: [],
  hasTechnicalError: false,
  columns: [],
  editState: { items: {} },
  underlyingsDetails: {},
};
